import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Usuario, TipoUsuario } from '../../types';
import usuarioService, { NovoUsuarioData, AtualizarUsuarioData } from '../../services/usuarioService';
import { toast } from 'react-toastify';
import { useDepartamento } from '../../contexts/DepartamentoContext';
import UsuarioEstatisticas, { UsuarioEstatisticasRef } from '../usuarios/UsuarioEstatisticas';
import GerenciamentoLiderancas from './GerenciamentoLiderancas';

// Componente para gerenciamento de usuários (apenas para administradores)
const GerenciamentoUsuarios: React.FC = () => {
  const { usuario } = useAuth();
  const { departamentos, obterDepartamentoPorId } = useDepartamento();
  
  // Estado para armazenar a lista de usuários
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [carregando, setCarregando] = useState<boolean>(true);
  const [termoBusca, setTermoBusca] = useState<string>('');
  
  // Estado para o formulário de novo usuário
  const [novoUsuario, setNovoUsuario] = useState<NovoUsuarioData>({
    nome: '',
    email: '',
    cargo: '',
    departamento_id: '',
    tipo: 'comum'
  });
  
  // Estado para o usuário selecionado para edição
  const [usuarioSelecionado, setUsuarioSelecionado] = useState<Usuario | null>(null);
  
  // Estado para controle de modais
  const [mostrarModalNovo, setMostrarModalNovo] = useState<boolean>(false);
  const [mostrarModalEdicao, setMostrarModalEdicao] = useState<boolean>(false);
  const [mostrarModalSenha, setMostrarModalSenha] = useState<boolean>(false);
  const [mostrarModalExcluir, setMostrarModalExcluir] = useState<boolean>(false);
  const [mostrarModalLiderancas, setMostrarModalLiderancas] = useState<boolean>(false);
  const [mostrarModalDesativar, setMostrarModalDesativar] = useState<boolean>(false);
  const [mostrarModalResetSenha, setMostrarModalResetSenha] = useState<boolean>(false);
  
  // Estado para exibir senha temporária após criação ou reset
  const [senhaNova, setSenhaNova] = useState<string>();
  
  // Referência para o componente de estatísticas
  const estatisticasRef = useRef<UsuarioEstatisticasRef>(null);
  const [atualizacaoEstatisticas, setAtualizacaoEstatisticas] = useState<number>(0);
  
  // Carregar a lista de usuários ao montar o componente
  useEffect(() => {
    carregarUsuarios();
  }, []);
  
  // Função para carregar a lista de usuários
  const carregarUsuarios = async () => {
    setCarregando(true);
    try {
      // Usando o novo método para obter dados completos dos usuários para administração
      const resultado = await usuarioService.obterTodosAdmin();
      if (resultado.success && resultado.usuarios) {
        setUsuarios(resultado.usuarios);
      } else {
        toast.error(resultado.message || 'Erro ao carregar usuários');
      }
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      toast.error('Erro ao carregar usuários');
    } finally {
      setCarregando(false);
    }
  };
  
  // Manipulador de mudança nos campos do formulário de novo usuário
  const handleNovoUsuarioChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    if (name === 'tipo') {
      setNovoUsuario(prev => ({
        ...prev,
        [name]: value as TipoUsuario
      }));
    } else {
      setNovoUsuario(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleUsuarioEdicaoChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    setUsuarioSelecionado(prev => prev ? {
      ...prev,
      [name]: name === 'tipo' ? value as TipoUsuario : value
    } : null);
  };
  
  // Função para abrir o modal de edição
  const abrirModalEdicao = (usuario: Usuario) => {
    setUsuarioSelecionado(usuario);
    setMostrarModalEdicao(true);
  };
  
  // Função para abrir o modal de confirmação de reset de senha
  const abrirModalResetSenha = (usuario: Usuario) => {
    setUsuarioSelecionado(usuario);
    setMostrarModalResetSenha(true);
  };
  
  // Função para abrir o modal de gerenciamento de lideranças
  const abrirModalLiderancas = (usuario: Usuario) => {
    setUsuarioSelecionado(usuario);
    setMostrarModalLiderancas(true);
  };

  // Função para abrir o modal de confirmação de desativação
  const abrirModalDesativar = (usuario: Usuario) => {
    setUsuarioSelecionado(usuario);
    setMostrarModalDesativar(true);
  };
  
  // Função para abrir o modal de confirmação de exclusão
  const abrirModalExcluir = (usuario: Usuario) => {
    setUsuarioSelecionado(usuario);
    setMostrarModalExcluir(true);
  };
  
  // Função para criar um novo usuário
  const criarUsuario = async () => {
    try {
      // Validar campos obrigatórios
      if (!novoUsuario.nome || !novoUsuario.email || !novoUsuario.cargo || !novoUsuario.departamento_id) {
        toast.error('Preencha todos os campos obrigatórios');
        return;
      }
      
      const resultado = await usuarioService.criarUsuario(novoUsuario);
      
      if (resultado.success && resultado.usuario) {
        toast.success('Usuário criado com sucesso!');
        setMostrarModalNovo(false);
        carregarUsuarios();
        
        // Atualizar estatísticas
        if (estatisticasRef.current) {
          estatisticasRef.current.atualizarEstatisticas();
        } else {
          setAtualizacaoEstatisticas(prev => prev + 1);
        }
        
        // Exibir a senha temporária
        if (resultado.senha_temporaria) {
          setSenhaNova(resultado.senha_temporaria);
          setMostrarModalSenha(true);
        }
        
        // Resetar o formulário
        setNovoUsuario({
          nome: '',
          email: '',
          cargo: '',
          departamento_id: '',
          tipo: 'comum'
        });
      } else {
        toast.error(resultado.message || 'Erro ao criar usuário');
      }
    } catch (error) {
      console.error('Erro ao criar usuário:', error);
      toast.error('Erro ao criar usuário');
    }
  };
  
  // Função para atualizar um usuário existente
  const atualizarUsuario = async () => {
    if (!usuarioSelecionado) return;
    
    try {
      const dadosAtualizacao: AtualizarUsuarioData = {
        nome: usuarioSelecionado.nome,
        email: usuarioSelecionado.email,
        cargo: usuarioSelecionado.cargo,
        departamento_id: usuarioSelecionado.departamento_id,
        tipo: usuarioSelecionado.tipo as TipoUsuario
        // NOTA: eh_lider é calculado automaticamente pelo backend
        // baseado nas lideranças na tabela departamento_lideres
      };
      
      const resultado = await usuarioService.atualizarUsuario(usuarioSelecionado.id, dadosAtualizacao);
      
      if (resultado.success) {
        toast.success('Usuário atualizado com sucesso!');
        setMostrarModalEdicao(false);
        carregarUsuarios();
        
        // Atualizar estatísticas
        if (estatisticasRef.current) {
          estatisticasRef.current.atualizarEstatisticas();
        } else {
          setAtualizacaoEstatisticas(prev => prev + 1);
        }
      } else {
        toast.error(resultado.message || 'Erro ao atualizar usuário');
      }
    } catch (error) {
      console.error('Erro ao atualizar usuário:', error);
      toast.error('Erro ao atualizar usuário');
    }
  };
  
  // Função para resetar a senha de um usuário
  const resetarSenha = async () => {
    if (!usuarioSelecionado) return;
    
    try {
      const resultado = await usuarioService.resetarSenha(usuarioSelecionado.id);
      
      if (resultado.success && resultado.senha_temporaria) {
        toast.success('Senha resetada com sucesso!');
        setMostrarModalResetSenha(false);
        
        // Exibir a senha temporária
        setSenhaNova(resultado.senha_temporaria);
        setMostrarModalSenha(true);
      } else {
        toast.error(resultado.message || 'Erro ao resetar senha');
      }
    } catch (error) {
      console.error('Erro ao resetar senha:', error);
      toast.error('Erro ao resetar senha');
    }
  };
  
  // Função para desativar/ativar um usuário
  const alterarStatusUsuario = async () => {
    if (!usuarioSelecionado) return;
    
    try {
      const novoStatus = !usuarioSelecionado.ativo;
      const resultado = await usuarioService.atualizarUsuario(usuarioSelecionado.id, { ativo: novoStatus });
      
      if (resultado.success) {
        const acao = novoStatus ? 'ativado' : 'desativado';
        toast.success(`Usuário ${acao} com sucesso!`);
        setMostrarModalDesativar(false);
        carregarUsuarios();
        
        // Atualizar estatísticas
        if (estatisticasRef.current) {
          estatisticasRef.current.atualizarEstatisticas();
        } else {
          setAtualizacaoEstatisticas(prev => prev + 1);
        }
      } else {
        toast.error(resultado.message || `Erro ao ${usuarioSelecionado.ativo ? 'desativar' : 'ativar'} usuário`);
      }
    } catch (error) {
      console.error('Erro ao alterar status do usuário:', error);
      toast.error(`Erro ao ${usuarioSelecionado.ativo ? 'desativar' : 'ativar'} usuário`);
    }
  };
  
  // Função para excluir um usuário
  const excluirUsuario = async () => {
    if (!usuarioSelecionado) return;
    
    try {
      const resultado = await usuarioService.excluirUsuario(usuarioSelecionado.id);
      
      if (resultado.success) {
        toast.success(`Usuário ${usuarioSelecionado.nome} excluído com sucesso!`);
        setMostrarModalExcluir(false);
        carregarUsuarios();
        
        // Atualizar estatísticas
        if (estatisticasRef.current) {
          estatisticasRef.current.atualizarEstatisticas();
        } else {
          setAtualizacaoEstatisticas(prev => prev + 1);
        }
      } else {
        toast.error(resultado.message || 'Erro ao excluir usuário');
      }
    } catch (error) {
      console.error('Erro ao excluir usuário:', error);
      toast.error('Erro ao excluir usuário');
    }
  };
  
  // Verificar se o usuário atual é administrador
  if (usuario?.tipo !== 'admin') {
    return (
      <div className="p-6 bg-white rounded-lg shadow-md">
        <h2 className="text-xl font-semibold text-red-600">Acesso Negado</h2>
        <p className="mt-2 text-gray-600">Você não tem permissão para acessar esta página.</p>
      </div>
    );
  }
  
  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-bravo-dark">Gerenciamento de Usuários</h1>
        <button
          onClick={() => setMostrarModalNovo(true)}
          className="bg-bravo-primary text-white px-4 py-2 rounded-md hover:bg-bravo-primary-dark transition-colors flex items-center"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
          Novo Usuário
        </button>
      </div>
      
      {/* Componente de estatísticas de usuários */}
      <UsuarioEstatisticas ref={estatisticasRef} atualizacao={atualizacaoEstatisticas} />

      {/* Barra de busca */}
      <div className="my-4 relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <input
          type="text"
          placeholder="Buscar por nome, email, cargo ou departamento..."
          value={termoBusca}
          onChange={(e) => setTermoBusca(e.target.value)}
          className="block w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-bravo-primary focus:border-bravo-primary text-sm"
        />
        {termoBusca && (
          <button
            onClick={() => setTermoBusca('')}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {carregando ? (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-bravo-primary"></div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          {(() => {
            const termo = termoBusca.toLowerCase().trim();
            const usuariosFiltrados = termo
              ? usuarios.filter(u =>
                  u.nome?.toLowerCase().includes(termo) ||
                  u.email?.toLowerCase().includes(termo) ||
                  u.cargo?.toLowerCase().includes(termo) ||
                  (u.departamento_nome || obterDepartamentoPorId(u.departamento_id || '')?.nome || '').toLowerCase().includes(termo)
                )
              : usuarios;
            return (
          <table className="min-w-full bg-white border border-gray-200">
            <thead>
              <tr>
                <th className="py-3 px-4 text-sm font-medium text-gray-900 text-left">Nome</th>
                <th className="py-3 px-4 text-sm font-medium text-gray-900 text-left">Email</th>
                <th className="py-3 px-4 text-sm font-medium text-gray-900 text-left">Cargo</th>
                <th className="py-3 px-4 text-sm font-medium text-gray-900 text-left">Departamento</th>
                <th className="py-3 px-4 text-sm font-medium text-gray-900 text-left">Lideranças</th>
                <th className="py-3 px-4 text-sm font-medium text-gray-900 text-left">Tipo</th>
                <th className="py-3 px-4 text-sm font-medium text-gray-900 text-left">Status</th>
                <th className="py-3 px-4 text-sm font-medium text-gray-900 text-center">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {usuariosFiltrados.map(usuario => (
                <tr key={usuario.id} className="hover:bg-gray-50">
                  <td className="py-3 px-4 text-sm text-gray-900">{usuario.nome}</td>
                  <td className="py-3 px-4 text-sm text-gray-900">{usuario.email}</td>
                  <td className="py-3 px-4 text-sm text-gray-900">{usuario.cargo}</td>
                  <td className="py-3 px-4 text-sm text-gray-900">
                    {usuario.departamento_nome || (usuario.departamento_id && obterDepartamentoPorId(usuario.departamento_id)?.nome) || 'Não definido'}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-900">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 cursor-pointer" onClick={() => abrirModalLiderancas(usuario)}>
                      Gerenciar
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-900">
                    {usuario.tipo === 'admin' ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        Admin
                      </span>
                    ) : usuario.tipo === 'expert' ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                        Expert
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Comum
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-900">
                    {usuario.ativo !== false ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Ativo
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        Inativo
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-900 text-center">
                    <div className="flex justify-center space-x-2">
                      <button
                        onClick={() => abrirModalEdicao(usuario)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Editar"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => abrirModalLiderancas(usuario)}
                        className="text-purple-600 hover:text-purple-900"
                        title="Gerenciar Lideranças"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => abrirModalResetSenha(usuario)}
                        className="text-yellow-600 hover:text-yellow-900"
                        title="Resetar senha"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => abrirModalDesativar(usuario)}
                        className={usuario.ativo !== false ? "text-red-600 hover:text-red-900" : "text-green-600 hover:text-green-900"}
                        title={usuario.ativo !== false ? "Desativar" : "Ativar"}
                      >
                        {usuario.ativo !== false ? (
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        )}
                      </button>
                      <button
                        onClick={() => abrirModalExcluir(usuario)}
                        className="text-red-700 hover:text-red-900"
                        title="Excluir"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              
              {usuariosFiltrados.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-4 px-4 text-center text-gray-500">
                    {termoBusca ? `Nenhum usuário encontrado para "${termoBusca}"` : 'Nenhum usuário encontrado'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
            );
          })()}
        </div>
      )}
      
      {/* Modal para novo usuário */}
      {mostrarModalNovo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold mb-4">Novo Usuário</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Nome</label>
                <input
                  type="text"
                  name="nome"
                  value={novoUsuario.nome}
                  onChange={handleNovoUsuarioChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-bravo-primary focus:border-bravo-primary"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  name="email"
                  value={novoUsuario.email}
                  onChange={handleNovoUsuarioChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-bravo-primary focus:border-bravo-primary"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Cargo</label>
                <input
                  type="text"
                  name="cargo"
                  value={novoUsuario.cargo}
                  onChange={handleNovoUsuarioChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-bravo-primary focus:border-bravo-primary"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Departamento</label>
                <select
                  name="departamento_id"
                  value={novoUsuario.departamento_id}
                  onChange={handleNovoUsuarioChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-bravo-primary focus:border-bravo-primary"
                >
                  <option value="">Selecione um departamento</option>
                  {departamentos.map(departamento => (
                    <option key={departamento.id} value={departamento.id}>
                      {departamento.nome}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="flex items-center">
                <p className="text-sm text-gray-700 italic">
                  Para definir lideranças, use a opção "Gerenciar Lideranças" após criar o usuário.
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Tipo de usuário</label>
                <div className="mt-2 space-x-4">
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      name="tipo"
                      value="comum"
                      checked={novoUsuario.tipo === 'comum'}
                      onChange={handleNovoUsuarioChange}
                      className="h-4 w-4 text-bravo-primary focus:ring-bravo-primary border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Comum</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      name="tipo"
                      value="admin"
                      checked={novoUsuario.tipo === 'admin'}
                      onChange={handleNovoUsuarioChange}
                      className="h-4 w-4 text-bravo-primary focus:ring-bravo-primary border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Administrador</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      name="tipo"
                      value="expert"
                      checked={novoUsuario.tipo === 'expert'}
                      onChange={handleNovoUsuarioChange}
                      className="h-4 w-4 text-bravo-primary focus:ring-bravo-primary border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Expert</span>
                  </label>
                </div>
                {novoUsuario.tipo === 'expert' && (
                  <p className="mt-2 text-sm text-orange-600 italic">
                    ⚠️ Usuário Expert terá acesso limitado: poderá apenas criar tarefas para o departamento de Tráfego.
                  </p>
                )}
              </div>
            </div>
            
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setMostrarModalNovo(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={criarUsuario}
                className="px-4 py-2 bg-bravo-primary text-white rounded-md hover:bg-bravo-primary-dark transition-colors"
              >
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal para edição de usuário */}
      {mostrarModalEdicao && usuarioSelecionado && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold mb-4">Editar Usuário</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Nome</label>
                <input
                  type="text"
                  name="nome"
                  value={usuarioSelecionado.nome}
                  onChange={handleUsuarioEdicaoChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-bravo-primary focus:border-bravo-primary"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  name="email"
                  value={usuarioSelecionado.email}
                  onChange={handleUsuarioEdicaoChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-bravo-primary focus:border-bravo-primary"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Cargo</label>
                <input
                  type="text"
                  name="cargo"
                  value={usuarioSelecionado.cargo}
                  onChange={handleUsuarioEdicaoChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-bravo-primary focus:border-bravo-primary"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Departamento</label>
                <select
                  name="departamento_id"
                  value={usuarioSelecionado.departamento_id || ''}
                  onChange={handleUsuarioEdicaoChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-bravo-primary focus:border-bravo-primary"
                >
                  <option value="">Selecione um departamento</option>
                  {departamentos.map(departamento => (
                    <option key={departamento.id} value={departamento.id}>
                      {departamento.nome}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="flex items-center">
                <p className="text-sm text-gray-700 italic">
                  Para gerenciar lideranças, use a opção "Gerenciar Lideranças" na tabela de usuários.
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Tipo de usuário</label>
                <div className="mt-2 space-x-4">
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      name="tipo"
                      value="comum"
                      checked={usuarioSelecionado.tipo === 'comum'}
                      onChange={handleUsuarioEdicaoChange}
                      className="h-4 w-4 text-bravo-primary focus:ring-bravo-primary border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Comum</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      name="tipo"
                      value="admin"
                      checked={usuarioSelecionado.tipo === 'admin'}
                      onChange={handleUsuarioEdicaoChange}
                      className="h-4 w-4 text-bravo-primary focus:ring-bravo-primary border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Administrador</span>
                  </label>
                </div>
              </div>
            </div>
            
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setMostrarModalEdicao(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={atualizarUsuario}
                className="px-4 py-2 bg-bravo-primary text-white rounded-md hover:bg-bravo-primary-dark transition-colors"
              >
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal para confirmação de reset de senha */}
      {mostrarModalResetSenha && usuarioSelecionado && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Resetar Senha</h3>
            <p className="text-gray-600 mb-4">
              Tem certeza que deseja resetar a senha do usuário <span className="font-medium">{usuarioSelecionado.nome}</span>?
              Uma nova senha temporária será gerada.
            </p>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setMostrarModalResetSenha(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={resetarSenha}
                className="px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 transition-colors"
              >
                Resetar Senha
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal para confirmação de desativação/ativação */}
      {mostrarModalDesativar && usuarioSelecionado && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              {usuarioSelecionado.ativo !== false ? 'Desativar' : 'Ativar'} Usuário
            </h3>
            <p className="text-gray-600 mb-4">
              Tem certeza que deseja {usuarioSelecionado.ativo !== false ? 'desativar' : 'ativar'} o usuário <span className="font-medium">{usuarioSelecionado.nome}</span>?
            </p>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setMostrarModalDesativar(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={alterarStatusUsuario}
                className={usuarioSelecionado.ativo !== false ? 
                  "px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors" :
                  "px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors"
                }
              >
                {usuarioSelecionado.ativo !== false ? 'Desativar' : 'Ativar'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal para exibição de senha temporária */}
      {mostrarModalSenha && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Senha Temporária</h3>
            <p className="text-gray-600 mb-2">
              A senha temporária foi gerada com sucesso. Informe ao usuário que ele deve alterá-la no primeiro acesso.
            </p>
            
            <div className="mt-4 p-3 bg-gray-100 rounded-md">
              <p className="text-center font-mono text-lg">{senhaNova}</p>
            </div>
            
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setMostrarModalSenha(false)}
                className="px-4 py-2 bg-bravo-primary text-white rounded-md hover:bg-bravo-primary-dark transition-colors"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal para confirmação de exclusão */}
      {mostrarModalExcluir && usuarioSelecionado && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4 text-red-600">Excluir Usuário</h3>
            <p className="text-gray-600 mb-2">
              Tem certeza que deseja <span className="font-bold text-red-600">excluir permanentemente</span> o usuário <span className="font-medium">{usuarioSelecionado.nome}</span>?
            </p>
            <p className="text-gray-600 mb-4 text-sm bg-yellow-50 p-3 rounded-md border border-yellow-200">
              Esta ação não pode ser desfeita. Todos os dados associados a este usuário serão removidos do sistema.
            </p>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setMostrarModalExcluir(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={excluirUsuario}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
              >
                Excluir Permanentemente
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para gerenciamento de lideranças */}
      {mostrarModalLiderancas && usuarioSelecionado && (
        <GerenciamentoLiderancas
          usuario={usuarioSelecionado}
          onClose={() => setMostrarModalLiderancas(false)}
          onUpdate={() => {
            carregarUsuarios();
            setAtualizacaoEstatisticas(prev => prev + 1);
          }}
        />
      )}
    </div>
  );
};

export default GerenciamentoUsuarios;
